import type { LanguageMode } from "../doc/languageMode.js";
import { escapeHtml } from "./escapeHtml.js";
import { hljs } from "./hljsRegister.js";
import { languageModeToHljsLang } from "./languageModeToHljs.js";
import {
  injectProseSpellHighlightIntoMarkdownHljs,
  languageModeUsesProseSpellHighlight,
  renderPlaintextLineWithProseSpellHighlight,
} from "./proseSpellDecorate.js";

export type RenderHighlightOptions = {
  /** When true, prose layers get red misspelling + green sentence marks (Normal text / Max lines; Markdown after hljs). */
  proseSpellDecorate?: boolean;
};

/** HTML fragment for the highlight `<code>` element (trusted spans from highlight.js). */
export function renderHighlightedFragment(
  text: string,
  mode: LanguageMode,
  options?: RenderHighlightOptions,
): string {
  const lang = languageModeToHljsLang(mode);
  if (lang === null) {
    if (
      options?.proseSpellDecorate === true &&
      languageModeUsesProseSpellHighlight(mode)
    ) {
      const lines = text.split("\n");
      return lines.map((line) => renderPlaintextLineWithProseSpellHighlight(line, mode)).join("\n");
    }
    return escapeHtml(text);
  }
  try {
    if (!hljs.getLanguage(lang)) {
      return escapeHtml(text);
    }
    const hl = hljs.highlight(text, { language: lang, ignoreIllegals: true }).value;
    if (mode === "md" && options?.proseSpellDecorate === true) {
      return injectProseSpellHighlightIntoMarkdownHljs(hl);
    }
    return hl;
  } catch {
    return escapeHtml(text);
  }
}

export function highlightCodeClassFor(mode: LanguageMode): string {
  const lang = languageModeToHljsLang(mode);
  if (lang === null) return "hljs npp-plaintext";
  return `hljs language-${lang}`;
}

/**
 * Wrap each line of hljs-highlighted HTML in its own `<div class="hl-line">`,
 * properly re-opening/closing any `<span>` tags that straddle newlines.
 */
export function wrapHighlightLines(hlHtml: string): string {
  const lines = hlHtml.split("\n");
  const openStack: string[] = [];
  const parts: string[] = new Array(lines.length);

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const reopen = openStack.map((t) => `<${t}>`).join("");

    const tagRe = /<(\/?)span([^>]*)>/g;
    let m: RegExpExecArray | null;
    while ((m = tagRe.exec(raw)) !== null) {
      if (m[1] === "/") openStack.pop();
      else openStack.push(`span${m[2]}`);
    }

    const close = openStack
      .slice()
      .reverse()
      .map(() => "</span>")
      .join("");
    const display = raw.length === 0 ? "\u200b" : raw;
    parts[i] = `<div class="hl-line">${reopen}${display}${close}</div>`;
  }

  return parts.join("");
}
